import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Heart, MapPin, Clock, Wrench, Zap } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useFavorites, useToggleFavorite } from "@/hooks/useAnnonces";
import ArtisanBottomNav from "@/components/ArtisanBottomNav";
import ClientBottomNav from "@/components/ClientBottomNav";
import { useSEO } from "@/hooks/useSEO";
import { DirectionalArrow } from "@/components/DirectionalArrow";
import { useLanguage } from "@/hooks/useLanguage";
import { useLocalizedPath } from "@/hooks/useLocalizedNavigate";

const Favoris = () => {
  useSEO({ noindex: true });
  const { t } = useLanguage("common");
  const lp = useLocalizedPath();
  const { user } = useAuth();
  const { data: favorites = [], isLoading } = useFavorites();
  const toggleFavorite = useToggleFavorite();

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="container py-8">
        <div className="mb-8">
          <h1 className="font-display text-3xl font-bold">{t("favoris.title_annonces")}</h1>
          <p className="mt-1 text-muted-foreground">
            {t("favoris.count_annonces", { count: favorites.length })}
          </p>
        </div>

        {isLoading ? (
          <p className="text-center text-muted-foreground py-12">{t("favoris.loading")}</p>
        ) : favorites.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Heart className="mx-auto h-12 w-12 text-muted-foreground/30" />
              <p className="mt-3 text-muted-foreground">{t("favoris.no_annonces")}</p>
              <Link to={lp("/dashboard")} className="mt-4 inline-block">
                <Button variant="outline">{t("favoris.browse_annonces")}</Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {favorites.map((fav: any) => {
              const annonce = fav.annonces;
              if (!annonce) return null;
              return (
                <Card key={fav.id} className="group overflow-hidden transition-all hover:shadow-lg hover:border-primary/20">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="space-y-2 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge variant="secondary" className="text-xs">
                            <Wrench className="me-1 h-3 w-3" />
                            {t(`annonces:categories_all.${annonce.category || annonce.metier}`, { defaultValue: annonce.category || annonce.metier })}
                          </Badge>
                        </div>
                        <h3 className="font-display text-lg font-semibold leading-tight">
                          {annonce.title}
                        </h3>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => toggleFavorite.mutate(annonce.id)}
                        className="shrink-0"
                      >
                        <Heart className="h-4 w-4 fill-destructive text-destructive" />
                      </Button>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <MapPin className="h-3.5 w-3.5" />
                        <span>{annonce.commune}, {annonce.wilaya}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5" />
                        <span>{annonce.delai}</span>
                      </div>
                    </div>
                    <div className="mt-3 border-t pt-3 text-end">
                      <Link to={lp(`/annonce/${annonce.id}`)}>
                        <Button variant="ghost" size="sm">
                          {t("favoris.see_details")} <DirectionalArrow className="ms-1 h-4 w-4" />
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>
      <Footer />
      <ArtisanBottomNav />
      <ClientBottomNav />
    </div>
  );
};

export default Favoris;
